/** GERP Sales Contract(136) — 레거시 appr_edit_forms_sales_contract_mstr */
export const SALES_CONTRACT_TYPE = 136;

export const isSalesContractType = (apvType: number): boolean =>
  apvType === SALES_CONTRACT_TYPE;

export const SALES_CONTRACT_MISSING_MESSAGE =
  '데이터를 로드하지 않았습니다. 조회 조건 선택 후 확인 버튼을 누르세요.';

/** GERP 미상신 결재 건 — 키는 GERP 응답 그대로 */
export type SalesContractCandidate = {
  sysId: string;
  aprvId: string;
  aprvTypcd: string;
  aprvRev?: string | null;
  title?: string | null;
  docTit?: string | null;
};

type Value = string | number | null | undefined;

/**
 * 계약 정보 — GERP 응답은 salesContrNo·poVdCd·vdNm·docTit 만 확정.
 * 나머지는 레거시 IMP_SALES_CONTR_HD 열 이름(camelCase)으로 오면 그대로 표시
 */
export type SalesContractHeader = {
  salesContrNo?: Value;
  poVdCd?: Value;
  vdNm?: Value;
  docTit?: Value;
  brand?: Value;
  originCd?: Value;
  inctType?: Value;
  currCd?: Value;
  salesContrDate?: Value;
  payTerm?: Value;
  sPort?: Value;
  salesMainfinishsched?: Value;
  packStd?: Value;
};

export type SalesContractDetail = {
  seasonNum?: Value;
  seasonRowspan?: Value;
  brand?: Value;
  season?: Value;
  styleNo?: Value;
  /** 레거시 STYLE_PO_NO — GERP 응답에 아직 없음 */
  stylePoNo?: Value;
  colorCd?: Value;
  unitPrice?: Value;
};

/** OZ 리포트 form POST 값 */
export type SalesContractOzPrint = {
  url?: string | null;
  gerpOzrNm?: string | null;
  gerpOdiNm?: string | null;
  pcount?: Value;
  args1?: string | null;
};

export type SalesContractCard = {
  aprvId: string;
  salesContrNo?: string | null;
  header?: SalesContractHeader | null;
  details?: SalesContractDetail[] | null;
  ozPrint?: SalesContractOzPrint | null;
};

export type SalesContractDraftSource = {
  code: number;
  attributes: Record<string, unknown>;
  display: {
    candidates: SalesContractCandidate[] | null;
    selection: string[] | null;
    subject: string | null;
    content: { cards?: SalesContractCard[] | null } | null;
  };
};

/** 작성 중 고른 건 1개 */
export type SalesContractItem = {
  aprvId: string;
  /** 레거시 data-sales_contr_no — 건 제목(DOC_TIT) */
  label: string;
  /** 머리글 차수 — 미상신 목록에만 있음 */
  aprvRev: string;
  card: SalesContractCard;
};

/** 저장 attributes 와 같은 모양 */
export type SalesContractState = {
  systemId: string;
  approvalTypeCode: string;
  /** 고른 건, 쉼표로 이음 */
  approvalId: string;
  /** 작성 시점 카드 HTML */
  content: string;
};

/** 레거시 콤보 문구 — 건 제목(DOC_TIT) */
export const buildSalesContractLabel = (c: SalesContractCandidate) =>
  c.docTit?.trim() || c.title?.trim() || c.aprvId;

/** 레거시 change_title_content — 고른 순서의 첫 건 기준. "외" 앞 공백 없음도 레거시 그대로 */
export const buildSalesContractSubject = (
  labels: string[],
  t: (key: string, options?: Record<string, unknown>) => string,
): string => {
  if (!labels.length) return '';
  const [first] = labels;
  if (labels.length === 1)
    return t('Sales Contract({{title}})', { title: first });
  return t('Sales Contract({{title}}외 {{count}}건)', {
    title: first,
    count: labels.length - 1,
  });
};

/** 레거시 본문 — 고를 때마다 덮어씀 */
export const buildSalesContractEditorContent = (
  labels: string[],
  t: (key: string) => string,
): string =>
  `<p>&nbsp;</p><p>${t('추가')} : ${labels.join(
    ',',
  )}&nbsp;</p><p>&nbsp;</p><p>&nbsp;</p>`;

/** 응답 카드 중 고른 건 — 없으면 첫 카드 */
export const pickSalesContractCard = (
  source: SalesContractDraftSource,
  aprvId: string,
): SalesContractCard | null => {
  const cards = source.display.content?.cards ?? [];
  return cards.find((c) => c.aprvId === aprvId) ?? cards[0] ?? null;
};

const nonEmpty = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() ? v : null;

export const parseSalesContractAttributes = (
  attributes: Record<string, unknown> | null | undefined,
): SalesContractState | null => {
  const content = nonEmpty(attributes?.content);
  if (!attributes || !content) return null;
  return {
    systemId: nonEmpty(attributes.systemId) ?? '',
    approvalTypeCode: nonEmpty(attributes.approvalTypeCode) ?? '',
    approvalId: nonEmpty(attributes.approvalId) ?? '',
    content,
  };
};

export const parseSalesContractContent = (
  attributes: Record<string, unknown> | null | undefined,
): string | null => nonEmpty(attributes?.content);

export const buildSalesContractAttributes = (
  state: SalesContractState | null | undefined,
) =>
  state
    ? {
        systemId: state.systemId,
        approvalTypeCode: state.approvalTypeCode,
        approvalId: state.approvalId,
        content: state.content,
      }
    : null;

export const checkSalesContractBeforeSave = (
  state: SalesContractState | null | undefined,
): string | null =>
  state?.content && state.approvalId ? null : SALES_CONTRACT_MISSING_MESSAGE;

export const salesContractKey = (
  state: SalesContractState | null | undefined,
) => state?.approvalId ?? '';

/** 레거시 문서 Print 버튼은 계약번호만 들고 있어 나머지는 레거시 고정값 */
export const LEGACY_SALES_CONTRACT_OZ: Required<
  Omit<SalesContractOzPrint, 'args1'>
> = {
  url: 'https://report.fnf.co.kr/report/gerp_oz.jsp',
  gerpOzrNm: 'gerp_sales_contract.ozr',
  gerpOdiNm: 'gerp_sales_contract',
  pcount: 1,
};

/** 레거시 btn-printsc — OZ 리포트를 새 창(SCMReport)에 form POST */
export const openSalesContractOzReport = (oz: SalesContractOzPrint) => {
  const url = oz.url?.trim() ?? '';
  // 저장 HTML 값이라 OZ 리포트 서버 주소만 허용
  if (
    !url.startsWith('https://report.fnf.co.kr/') ||
    !oz.gerpOdiNm ||
    !oz.args1
  )
    return;
  const target = 'SCMReport';
  window.open(
    '',
    target,
    'top=100,left=100,width=900,height=800,location=no,toolbar=no,status=no',
  );
  const form = document.createElement('form');
  form.method = 'post';
  form.action = url;
  form.target = target;
  const fields: Record<string, string> = {
    gerp_ozr_nm: oz.gerpOzrNm ?? '',
    gerp_odi_nm: oz.gerpOdiNm,
    [`odi.${oz.gerpOdiNm}.args1`]: oz.args1,
    [`odi.${oz.gerpOdiNm}.pcount`]: String(oz.pcount ?? 1),
  };
  Object.entries(fields).forEach(([name, value]) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
  form.remove();
};
